<template>
  <div class="setting-user">
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <div class="mb-x2" layout="layout">
      <el-button class="el-button-mt" type="default" @click="onChangeBackEvent"
        ><i class="mdi mdi-arrow-left"></i>戻る</el-button
      >
    </div>
    <!-- ユーザー情報-->
    <div class="card">
      <div class="caption">ユーザー情報</div>
      <div class="content">
        <div class="input-container">
          <div class="label">お名前</div>
          <div class="input">
            <el-input
              :disabled="!editMode"
              :class="{ success: $v.settings.name.required && !$v.settings.name.$invalid }"
              placeholder="お名前"
              :maxlength="60"
              type="text"
              v-model="settings.name"
              @input="inputChange('name')"
              tabindex="1"
            >
              <template slot="prepend"><i class="mdi mdi-account"></i></template>
            </el-input>
            <div class="input-invalid-text" v-if="!$v.settings.name.required && $v.settings.name.$error">
              お名前を入力してください。
            </div>
          </div>
        </div>
        <div class="input-container">
          <div class="label">メールアドレス</div>
          <div class="input">
            <el-input
              :disabled="!editMode"
              :class="{
                error: $v.settings.email.required && $v.settings.email.$error,
                success: $v.settings.email.required && !$v.settings.email.$invalid
              }"
              placeholder="メールアドレス"
              :maxlength="100"
              type="text"
              v-model="settings.email"
              @input="inputChange('email')"
              tabindex="2"
            >
              <template slot="prepend"><i class="mdi mdi-email"></i></template>
            </el-input>
            <div class="input-invalid-text" v-if="!$v.settings.email.required && $v.settings.email.$error">
              メールアドレスを入力してください。
            </div>
            <div class="input-invalid-text" v-if="!$v.settings.email.email && $v.settings.email.$error">
              メールアドレスの形式が正しくありません。
            </div>
          </div>
        </div>
        <div class="input-container">
          <div class="label">パスワード</div>
          <div class="input">
            <el-input
              :disabled="!editMode"
              :class="{ success: $v.settings.password.required && !$v.settings.password.$invalid }"
              placeholder="パスワード"
              :maxlength="100"
              type="password"
              v-model="settings.password"
              @keydown.native="passwordOnKeydown"
              tabindex="3"
            >
              <template slot="prepend"><i class="mdi mdi-key-variant"></i></template>
            </el-input>
            <div class="input-invalid-text" v-if="!$v.settings.password.required && $v.settings.password.$error">
              パスワードを入力してください。
            </div>
            <div class="input-invalid-text" v-if="!$v.settings.password.minLength && $v.settings.password.$error">
              パスワードは8文字以上にしてください。
            </div>
          </div>
        </div>
      </div>
      <div class="action" layout layout-align="end-center">
        <el-button v-if="!editMode" type="default" @click="onChangeEditMode">編集</el-button>
        <div v-if="editMode">
          <el-button type="default" @click="cancel">キャンセル</el-button>
          <el-button type="primary" @click="submit" :disabled="$v.$invalid === true">保存</el-button>
        </div>
      </div>
    </div>
    <!-- Facebookログイン連携-->
    <div class="card">
      <div class="caption">Facebookログイン連携</div>
      <div class="content">
        <div>
          <div class="basic">Facebookログイン連携を設定すると、</div>
          <div class="basic">次回からメールアドレスとパスワードの入力が不要になります。</div>
        </div>
      </div>
      <div class="action" layout>
        <el-button class="el-button--facebook" v-if="!settings.facebook" @click="facebookConnect"
          ><i class="mdi mdi-facebook-box"></i>Facebookと連携</el-button
        >
        <el-button class="el-button--facebook" v-if="settings.facebook" @click="facebookRemoveConnect"
          ><i class="mdi mdi-facebook-box"></i>Facebookとの連携を解除</el-button
        >
      </div>
    </div>
    <!-- 2段階認証-->
    <setting-user-two-factor></setting-user-two-factor>
    <!-- 通知メール設定-->
    <setting-user-notifications></setting-user-notifications>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.card {
  margin-bottom: $spacing-unit * 2;

  .caption {
    margin-bottom: $spacing-unit * 2;
  }

  .content {
    .basic {
      margin-bottom: $spacing-unit * 0.5;
    }
  }

  .action {
    margin-top: $spacing-unit * 3;

    .el-button--facebook {
      margin-left: auto;
    }
  }
}

.input-container {
  display: flex;
  align-content: center;
  align-items: center;
  max-width: 100%;
  margin-bottom: $spacing-unit * 2;

  .label {
    width: 150px;
  }

  .input {
    flex: 1;
  }
}
</style>
<script lang="ts">
import { mapState, mapActions } from 'pinia'
import Vue, { defineComponent } from 'vue'
import { email, minLength, required } from 'vuelidate/lib/validators'

import ConfirmDialog from '@/client/components/_atoms/confirm_dialog'
import api from '@/client/core/api'
import SettingUserNotifications from '@/client/features/setting_user/setting_user_notifications'
import SettingUserTwoFactor from '@/client/features/setting_user/setting_user_two_factor'
import { TrackingService } from '@/client/services'
import { useCampaignStore } from '@/client/store/useCampaignStore'
import { useSystemStore } from '@/client/store/useSystemStore'
import { getLoginStatus, login } from '@/client/utils/facebook_sdk'

export default defineComponent({
  components: {
    ConfirmDialog,
    SettingUserNotifications,
    SettingUserTwoFactor
  },
  data() {
    return {
      settings: {
        name: '',
        email: '',
        password: '********',
        facebook: null as { userId: string } | null
      },
      originSettings: {
        name: '',
        email: '',
        password: '********',
        facebook: null as { userId: string } | null
      },
      facebook: {} as { userId?: string },
      editMode: false,
      isInputPassword: false,
      isInput: false,
      isSubmit: false,
      dialog: {
        title: ''
      }
    }
  },
  async created() {
    const result = await api.get('/me/settings')

    if (result.data) {
      this.settings = Object.assign(this.settings, result.data.settings)
      this.originSettings = Object.assign(this.originSettings, result.data.settings)
    }

    window.onbeforeunload = () => {
      if (this.isInput) {
        return ''
      }
    }
  },
  destroyed() {
    window.onbeforeunload = null
  },
  computed: {
    ...mapState(useSystemStore, {
      user: state => state.user
    }),
    ...mapState(useCampaignStore, {
      lastQueryParams: state => state.lastQueryParams
    })
  },
  methods: {
    ...mapActions(useSystemStore, {
      setSystemUser: 'setUser'
    }),
    /**
     * submit
     */
    async submit() {
      // validations
      this.$v.$touch()
      if (this.$v.$invalid === true) {
        return
      }
      if (this.isSubmit) {
        return
      }
      this.isSubmit = true
      const params: any = {
        settings: {
          name: this.settings.name,
          email: this.settings.email,
          facebook: this.settings.facebook
        }
      }
      if (this.isInputPassword) {
        params.settings.password = this.settings.password
      }

      // ユーザー設定を変更
      const result = await api.put('/me/settings', params)
      this.isSubmit = false

      if (result.error && result.error.type === 'DUPLICATE') {
        // エラーメッセージを表示
        this.$notify({
          title: 'すでに登録済のメールアドレスです。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        return
      }
      if (!result.data) {
        // エラーメッセージを表示
        this.$notify({
          title: 'ユーザー情報の更新に失敗しました。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'ユーザー情報を更新しました。',
        message: '',
        customClass: 'success',
        duration: 5000
      })
      this.originSettings = Object.assign(this.originSettings, params.settings)
      this.cancel()

      // システムユーザーストアに変更
      const user = Object.assign({}, this.user, {
        name: this.settings.name,
        email: this.settings.email
      })
      this.setSystemUser(user)
    },

    /**
     * Facebook連携ボタンクリック
     */
    async facebookConnect() {
      TrackingService.sendEvent('click:ユーザー設定|Facebookと連携')

      let response = await getLoginStatus()

      if (response.status !== 'connected') {
        response = await login()
      }

      if (response.status !== 'connected') {
        this.$notify({
          title: 'Facebook認証に失敗しました。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })

        return
      }

      this.facebook = {
        userId: response.authResponse.userID
      }

      // Facebookとの連携をする
      return this.execFacebookConnect()
    },

    /**
     * Facebookとの連携をする
     */
    async execFacebookConnect() {
      if (this.isSubmit) {
        return
      }
      if (!this.facebook.userId) {
        return
      }
      const params = {
        settings: {
          facebook: {
            userId: this.facebook.userId
          }
        }
      }
      // ユーザー設定を変更
      const result = await api.put('/me/settings', params)
      this.isSubmit = false

      if (result.error && result.error.type === 'DUPLICATE') {
        // エラーメッセージを表示
        this.$notify({
          title: '別のユーザーで連携済みの為、連携は行なえません。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      if (!result.data) {
        // エラーメッセージを表示
        this.$notify({
          title: 'Facebookログイン連携設定に失敗しました。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'Facebookログイン連携を設定しました。',
        message: '',
        customClass: 'success',
        duration: 5000
      })
      this.settings.facebook = {
        userId: this.facebook.userId
      }
      this.originSettings = Object.assign(this.originSettings, params.settings)
      // システムユーザーストアに変更
      const user = Object.assign({}, this.user, {
        pictureUrl: result.data.pictureUrl
      })
      this.setSystemUser(user)
    },

    /**
     * Facebookとの連携を解除
     */
    async facebookRemoveConnect() {
      if (this.isSubmit) {
        return
      }
      ;(this.$refs.confirmDialog as InstanceType<typeof ConfirmDialog>).open(
        'Facebookログイン連携を解除してよろしいですか？',
        '',
        'default',
        async confirm => {
          if (!confirm) {
            return
          }
          const params = {
            settings: {}
          }
          // ユーザー設定を変更
          const result = await api.put('/me/settings', params)
          this.isSubmit = false

          if (!result.data) {
            // エラーメッセージを表示
            this.$notify({
              title: 'Facebookログイン連携解除に失敗しました。',
              message: '',
              customClass: 'danger',
              duration: 5000
            })
            return
          }

          // 成功メッセージを表示
          this.$notify({
            title: 'Facebookログイン連携を解除しました。',
            message: '',
            customClass: 'success',
            duration: 5000
          })
          this.settings.facebook = null
          this.originSettings = Object.assign(this.originSettings, params.settings)

          // システムユーザーストアに変更
          const user = Object.assign({}, this.user, {
            pictureUrl: result.data.pictureUrl
          })
          this.setSystemUser(user)
        }
      )
    },

    /**
     * パスワード変更イベント
     * @param {any} event イベント
     */
    passwordOnKeydown(event: any) {
      TrackingService.sendEvent('input:ユーザー設定|パスワード')

      // validate
      this.$v.settings.password.$touch()
      // 最初入力する場合は、パスワードを''にセットし、パスワード変更フラグをtrueにセット
      if (this.isInputPassword === false) {
        Vue.nextTick(() => {
          if (event.key.length === 1) {
            this.isInputPassword = true
          }
        })
      }
      this.isInput = true
    },

    /**
     * input変更イベント
     * @param {string} name
     */
    inputChange(name: string) {
      if (name === 'name') {
        TrackingService.sendEvent('input:ユーザー設定|お名前')
      } else TrackingService.sendEvent('input:ユーザー設定|メールアドレス')

      // validate
      this.$v.settings[name].$touch()
      this.isInput = true
    },

    /**
     * キャンセル
     */
    cancel() {
      this.editMode = false
      this.isInput = false
      this.isInputPassword = false
      this.settings = Object.assign({}, this.originSettings)
    },

    /**
     * 離脱チェック
     */
    checkBeforeLeave(callback: (err: any, isExit: boolean) => void) {
      if (!this.isInput) {
        return callback(null, true)
      }
      ;(this.$refs.confirmDialog as InstanceType<typeof ConfirmDialog>).open(
        '未保存の変更があります。このまま移動しますか？',
        '',
        'default',
        confirm => {
          if (!confirm) {
            return callback(null, false)
          }
          callback(null, true)
        }
      )
    },

    onChangeBackEvent() {
      TrackingService.sendEvent('click:ユーザー設定|戻る')

      // 保存されたクエリパラメータがあれば復元、なければそのまま
      const query = this.lastQueryParams || {}
      this.$router.push({ path: '/campaigns', query })
    },

    onChangeEditMode() {
      TrackingService.sendEvent('click:ユーザー設定|編集')

      this.editMode = true
    }
  },
  validations: {
    settings: {
      email: {
        required,
        email
      },
      name: {
        required
      },
      password: {
        required,
        // パスワードは8文字以上が必要
        minLength: minLength(8)
      }
    }
  }
})
</script>
